import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Location } from '@angular/common';
import { LearningService, LearningFilterURL } from '@shared/services/api/learning.service';
import { Observable, of, switchMap, tap, Subscription, forkJoin, skip, take } from 'rxjs';
import { Learning } from '@shared/entities/learning';
import { FilterSelectedEventType, FilterOrSortDropdownType } from '@shared/models/types';
import { Router } from '@angular/router';
import { LearningFilter } from '@shared/entities/learningFilter';
import { LearningFiltersService } from '@shared/services/api/learning-filters.service';
import { LanguageService } from '@shared/services';

@Component({
  selector: 'app-learning',
  templateUrl: './learning.component.html',
  styleUrl: './learning.component.scss'
})
export class LearningComponent implements OnInit, OnDestroy {
  showSuccess = false;
  showError = false;

  allLearning$: Observable<Learning[]>;
  learningFilter$: Observable<LearningFilter>;

  learningPrevPage?: number = undefined;
  learningNextPage?: number = undefined;
  learningCurrentPage = 1;
  learningTotalPages?: number = undefined;

  learningFilters: any = {};
  isLoadingLanguageChange = false;

  private languageSubscription?: Subscription;

  constructor(
    private _learningService: LearningService,
    private _learningFiltersService: LearningFiltersService,
    private _location: Location,
    private _router: Router,
    private _languageService: LanguageService,
    private _cdr: ChangeDetectorRef
  ) {
    this.allLearning$ = this._learningService.allLearning$.pipe(
      switchMap((paginatedResult) => {
        this.learningPrevPage = paginatedResult.prevPage;
        this.learningCurrentPage = paginatedResult.currentPage ?? 1;
        this.learningNextPage = paginatedResult.nextPage;
        this.learningTotalPages = paginatedResult.totalPage;
        return of(paginatedResult.data);
      })
    );

    this.learningFilter$ = this._learningFiltersService.all$;
  }

  goBack() {
    this._location.back();
  }

  goToRepository() {
    this._router.navigate(['/repository']);
  }

  ngOnInit(): void {
    this._learningService.refresh(1).subscribe();
    this._learningFiltersService.refresh(true).subscribe();

    let isFirstEmit = true;
    this.languageSubscription = this._languageService.current$.subscribe(() => {
      if (isFirstEmit) {
        isFirstEmit = false;
        return;
      }

      this.isLoadingLanguageChange = true;
      this._cdr.detectChanges();
      this.learningFilters = {};

      this._learningService.allLearning$.pipe(skip(1), take(1)).subscribe(() => {
        this.isLoadingLanguageChange = false;
        this._cdr.detectChanges();
      });

      forkJoin({
        learning: this._learningService.refresh(1),
        filters: this._learningFiltersService.refresh(true)
      }).subscribe({
        error: () => {
          this.isLoadingLanguageChange = false;
          this._cdr.detectChanges();
        }
      });
    });
  }

  onPageSelected(page: number) {
    this._learningService
      .refresh(page)
      .pipe(
        tap((paginatedResult) => {
          this.learningPrevPage = paginatedResult.prevPage;
          this.learningCurrentPage = paginatedResult.currentPage ?? 1;
          this.learningNextPage = paginatedResult.nextPage;
          this.learningTotalPages = paginatedResult.totalPage;
        })
      )
      .subscribe();
  }

  onFilterSelected(filters: FilterSelectedEventType[]) {
    const filtersName = filters.reduce((acc: any, filter: FilterSelectedEventType) => {
      acc[filter.name] = filter.selectedValue;
      return acc;
    }, {});

    this.learningFilters = { ...filtersName };
    this._learningService.filters = filtersName as LearningFilterURL;
    this._learningFiltersService.filters = filtersName as LearningFilterURL;
  }

  getFilterFields(filterData: any): FilterOrSortDropdownType[] {
    const fields: FilterOrSortDropdownType[] = [];

    if (!filterData) {
      return fields;
    }

    for (const key in filterData) {
      if (Object.prototype.hasOwnProperty.call(filterData, key) && key !== 'orderBy') {
        const values = filterData[key];
        if (values && Array.isArray(values)) {
          fields.push({
            key: key,
            values: values.map((value: string) => ({
              id: value,
              value: value
            }))
          });
        }
      }
    }

    return fields;
  }

  getSortingOptions(filterData: any): FilterOrSortDropdownType[] {
    const options: FilterOrSortDropdownType[] = [];

    if (!filterData || !filterData.orderBy) {
      return options;
    }

    options.push({
      key: 'orderBy',
      values: filterData.orderBy.map((value: string) => ({
        id: value,
        value: value
      }))
    });

    return options;
  }

  resetFilters() {
    this.learningFilters = {};
    this._learningService.filters = {} as LearningFilterURL;
    this._learningFiltersService.filters = {} as LearningFilterURL;
  }

  getYear() {
    return new Date().getFullYear();
  }

  ngOnDestroy(): void {
    // Unsubscribe dalla subscription della lingua
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }

    // Reset filters when leaving the page
    this.resetFilters();
  }
}
